import { z } from 'zod';
import type { PlatformRole } from '../enums.js';

/**
 * Console de plateforme — le rôle, et le compte désigné par un geste.
 *
 * **Aucun contrat de ce fichier ne donne un droit de plateforme, et c'est le point.** Le rôle vient
 * de la configuration du serveur — `PLATFORM_ADMIN_EMAILS`, `PLATFORM_SUPPORT_EMAILS` — et d'elle
 * seule : il n'y a ni route d'amorçage, ni changement de rôle depuis la console. Nommer un
 * administrateur suppose l'accès à la machine, ce qui est la barrière voulue devant le droit le
 * plus étendu du produit. Le reste de la console — statistiques, cabinets, comptes, tickets,
 * services externes — suppose le droit déjà acquis et vit dans les fichiers voisins.
 *
 * Deux règles tiennent l'ensemble, et elles ne se discutent pas :
 * — **aucune donnée patient ne passe par la console.** La V1 avait une route « tous les patients de
 *   tous les cabinets » ; l'audit l'a qualifiée de fuite, elle ne revient pas. Les lectures
 *   transversales rendent des agrégats et des métadonnées de cabinet, jamais un dossier.
 * — **lire est ouvert à `support` et `super_admin` ; modifier est réservé à `super_admin`**, avec
 *   une trace d'audit portant l'avant et l'après.
 */

/** Le compte visé par un geste de la console : désactivation, réactivation. */
export const platformUserIdParamSchema = z.object({ id: z.uuid() }).strict();

/**
 * Un compte vu sous l'angle de son rôle de plateforme, rendu par la synchronisation du rôle. C'est
 * le personnel — le nôtre ou celui d'un cabinet —, jamais un patient.
 */
export interface PlatformAdminDto {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  platformRole: PlatformRole;
  /** Rôle avant le geste. Égal à `platformRole` quand rien n'a changé. */
  previousPlatformRole: PlatformRole;
  updatedAt: string;
}
